import { useTranslation } from 'react-i18next';
import { Input } from '@/shared/components/ui/input';
import {
  parseLinearScaleOptions,
  serializeLinearScaleOptions,
  type LinearScaleOptions,
} from '../../adapters/field-type.adapter';

interface LinearScaleEditorProps {
  fieldId: string;
  optionsJson: string | null | undefined;
  onChange: (optionsJson: string) => void;
  disabled?: boolean;
}

const MIN_BOUND = 0;
const MAX_BOUND = 10;

function clamp(value: LinearScaleOptions): LinearScaleOptions {
  const min = Math.max(MIN_BOUND, Math.min(value.min, MAX_BOUND - 1));
  const max = Math.max(min + 1, Math.min(value.max, MAX_BOUND));
  return { ...value, min, max };
}

export function LinearScaleEditor({
  fieldId,
  optionsJson,
  onChange,
  disabled = false,
}: LinearScaleEditorProps) {
  const { t } = useTranslation('forms');
  const value = parseLinearScaleOptions(optionsJson ?? null);

  function update(next: Partial<LinearScaleOptions>) {
    onChange(serializeLinearScaleOptions(clamp({ ...value, ...next })));
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        {t('builder.linearScale.rangeLabel')}
      </p>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={MIN_BOUND}
          max={MAX_BOUND - 1}
          value={value.min}
          onChange={(e) => update({ min: Number(e.target.value) })}
          data-testid={`forms-builder-linear-scale-min-${fieldId}`}
          disabled={disabled}
          className="h-8 w-20 text-sm"
        />
        <span className="text-xs text-muted-foreground">{t('builder.linearScale.to')}</span>
        <Input
          type="number"
          min={MIN_BOUND + 1}
          max={MAX_BOUND}
          value={value.max}
          onChange={(e) => update({ max: Number(e.target.value) })}
          data-testid={`forms-builder-linear-scale-max-${fieldId}`}
          disabled={disabled}
          className="h-8 w-20 text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={value.minLabel ?? ''}
          onChange={(e) => update({ minLabel: e.target.value || undefined })}
          placeholder={t('builder.linearScale.minLabelPlaceholder')}
          data-testid={`forms-builder-linear-scale-min-label-${fieldId}`}
          disabled={disabled}
          className="h-8 text-sm"
        />
        <Input
          value={value.maxLabel ?? ''}
          onChange={(e) => update({ maxLabel: e.target.value || undefined })}
          placeholder={t('builder.linearScale.maxLabelPlaceholder')}
          data-testid={`forms-builder-linear-scale-max-label-${fieldId}`}
          disabled={disabled}
          className="h-8 text-sm"
        />
      </div>
    </div>
  );
}
